import { DEFAULT_PAGE_SIZE } from "@design-system/pagination";
import {
  infiniteQueryOptions,
  skipToken,
  useInfiniteQuery,
} from "@tanstack/react-query";

import { getCompanies } from "../api";
import type { CompaniesSearchParams } from "../schemas";

type CompaniesQueryParams = Omit<CompaniesSearchParams, "offset" | "limit">;

const companiesQueryOptions = {
  all: () => ["impersonation-companies"] as const,
  list: (params: CompaniesQueryParams | typeof skipToken) =>
    infiniteQueryOptions({
      queryKey: [...companiesQueryOptions.all(), params] as const,
      queryFn:
        params === skipToken
          ? skipToken
          : ({ signal, pageParam }) =>
              getCompanies(
                { ...params, offset: pageParam * DEFAULT_PAGE_SIZE },
                signal
              ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.length < DEFAULT_PAGE_SIZE ? undefined : lastPageParam + 1,
    }),
};

function useCompanies(params: CompaniesQueryParams | typeof skipToken) {
  const query = useInfiniteQuery(companiesQueryOptions.list(params));

  return {
    companies: query.data?.pages.flat() ?? [],
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

export { companiesQueryOptions, useCompanies };
export type { CompaniesQueryParams };
